import axios from 'axios';
import { toast } from 'react-toastify';

export const RECEIVING_WORKPERIODS = 'RECEIVING_WORKPERIODS';
export const RECEIVED_WORKPERIODS = 'RECEIVED_WORKPERIODS';
export const FAILURE_WORKPERIODS = 'FAILURE_WORKPERIODS';
export const CLEAR_WORKPERIODS = 'CLEAR_WORKPERIODS';

export const RECEIVING_WORKPERIOD_DETAIL = 'RECEIVING_WORKPERIOD_DETAIL';
export const RECEIVED_WORKPERIOD_DETAIL = 'RECEIVED_WORKPERIOD_DETAIL';
export const FAILURE_WORKPERIOD_DETAIL = 'FAILURE_WORKPERIOD_DETAIL';
export const CLEAR_WORKPERIOD_DETAIL = 'CLEAR_WORKPERIOD_DETAIL';

export const RECEIVING_WORKPERIOD_USERS = 'RECEIVING_WORKPERIOD_USERS';
export const RECEIVED_WORKPERIOD_USERS = 'RECEIVED_WORKPERIOD_USERS';
export const FAILURE_WORKPERIOD_USERS = 'FAILURE_WORKPERIOD_USERS';

export const ENDING_WORKPERIOD = 'ENDING_WORKPERIOD';
export const ENDED_WORKPERIOD = 'ENDED_WORKPERIOD';
export const FAILURE_END_WORKPERIOD = 'FAILURE_END_WORKPERIOD';

export const STARTING_WORKPERIOD = 'STARTING_WORKPERIOD';
export const STARTED_WORKPERIOD = 'STARTED_WORKPERIOD';
export const FAILURE_START_WORKPERIOD = 'FAILURE_START_WORKPERIOD';

export const DOWNLOADING_WORKPERIOD_CLOSEOUT = 'DOWNLOADING_WORKPERIOD_CLOSEOUT';
export const DOWNLOADED_WORKPERIOD_CLOSEOUT = 'DOWNLOADED_WORKPERIOD_CLOSEOUT';
export const FAILURE_WORKPERIOD_CLOSEOUT = 'FAILURE_WORKPERIOD_CLOSEOUT';

function getFileNameFromDisposition(disposition, fallback) {
    if (!disposition) return fallback;

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch (err) {
            return utf8Match[1];
        }
    }

    const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1] || fallback;
}

function triggerBrowserDownload(blob, fileName) {
    const pdfBlob = new Blob([blob], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
    // Revoke after a delay so the browser tab has time to load the PDF
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

function readBlobAsText(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => reject(new Error('Failed to read server response'));
        reader.readAsText(blob);
    });
}

async function getCloseoutErrorMessage(err) {
    const fallbackMessage = err?.message || 'Failed to download closeout summary';
    const responseData = err?.response?.data;

    if (!(responseData instanceof Blob)) {
        return responseData?.message || fallbackMessage;
    }

    try {
        const text = await readBlobAsText(responseData);
        if (!text) return fallbackMessage;

        try {
            const parsed = JSON.parse(text);
            return parsed?.message || parsed?.error || fallbackMessage;
        } catch (parseErr) {
            return text;
        }
    } catch (readErr) {
        return fallbackMessage;
    }
}

export function getWorkperiodsRequest(startDate, endDate) {
    return (dispatch) => {
        dispatch({ type: RECEIVING_WORKPERIODS });

        const endpoints = [
            '/api/UserAdmin/workperiods',
            '/api/useradmin/workperiods',
            '/api/useradmin/workperiods/list',
            '/api/useradmin/workperiod/list'
        ];

        const fetchFrom = (index) => {
            return axios.get(endpoints[index], {
                params: {
                    startDate,
                    endDate,
                    // Default API limit is 50; raise so date-range lists get tip-settlement flags for all rows.
                    limit: 200,
                    page: 1,
                }
            });
        };

        const handleResponse = (res) => {
            if (res.data?.status === 'Error') {
                toast.error(res.data?.message || 'Failed to load work periods');
                dispatch({ type: RECEIVED_WORKPERIODS, payload: [] });
                return;
            }

            const raw = res.data?.workperiods || res.data?.WorkPeriods || res.data?.Workperiods;
            const workperiods = Array.isArray(raw) ? raw : [];
            dispatch({ type: RECEIVED_WORKPERIODS, payload: workperiods });
        };

        const tryFetch = (index) => {
            fetchFrom(index)
                .then(handleResponse)
                .catch(err => {
                    const status = err?.response?.status;
                    if (status === 404 && index < endpoints.length - 1) {
                        tryFetch(index + 1);
                        return;
                    }

                    console.error('Error fetching work periods:', err);
                    toast.error('Failed to load work periods');
                    dispatch({ type: FAILURE_WORKPERIODS, payload: err?.message || 'Failed to load work periods' });
                });
        };

        tryFetch(0);
    };
}

export function clearWorkperiods() {
    return {
        type: CLEAR_WORKPERIODS
    };
}

export function getWorkperiodDetailRequest(periodId) {
    return (dispatch) => {
        dispatch({ type: RECEIVING_WORKPERIOD_DETAIL });

        axios.get(`/api/UserAdmin/workperiod/${periodId}`)
            .then((res) => {
                if (res.data?.status === 'Error') {
                    toast.error(res.data?.message || 'Failed to load work period detail');
                    dispatch({ type: RECEIVED_WORKPERIOD_DETAIL, payload: null });
                    return;
                }

                const detail = res.data?.workperiod || res.data?.WorkPeriod || res.data?.Workperiod || res.data || null;
                const linkedEvent = res.data?.event || res.data?.Event || null;
                const payload = detail && linkedEvent
                    ? { ...detail, event: linkedEvent }
                    : detail;
                dispatch({ type: RECEIVED_WORKPERIOD_DETAIL, payload });
            })
            .catch((err) => {
                console.error('Error fetching work period detail:', err);
                toast.error('Failed to load work period detail');
                dispatch({ type: FAILURE_WORKPERIOD_DETAIL, payload: err?.message || 'Failed to load work period detail' });
            });
    };
}

export function getWorkperiodUsersRequest(periodId) {
    return (dispatch) => {
        dispatch({ type: RECEIVING_WORKPERIOD_USERS });

        axios.get(`/api/UserAdmin/workperiod/${periodId}/users`)
            .then((res) => {
                if (res.data?.status === 'Error') {
                    toast.error(res.data?.message || 'Failed to load work period users');
                    dispatch({ type: RECEIVED_WORKPERIOD_USERS, payload: [] });
                    return;
                }

                const users = res.data?.users || res.data?.Users || [];
                dispatch({ type: RECEIVED_WORKPERIOD_USERS, payload: Array.isArray(users) ? users : [] });
            })
            .catch((err) => {
                console.error('Error fetching work period users:', err);
                toast.error('Failed to load work period users');
                dispatch({ type: FAILURE_WORKPERIOD_USERS, payload: err?.message || 'Failed to load work period users' });
            });
    };
}

export function clearWorkperiodDetail() {
    return {
        type: CLEAR_WORKPERIOD_DETAIL
    };
}

function toastWorkperiodClosePooledPayout(data) {
    const payout = data?.pooledPayout || data?.PooledPayout;
    if (!payout) {
        return;
    }
    const attempted = payout.attempted ?? payout.Attempted;
    if (attempted === false) {
        return;
    }
    const paid = Number(payout.paid ?? payout.Paid ?? 0);
    const failed = Number(payout.failed ?? payout.Failed ?? 0);
    const held = Number(payout.held ?? payout.Held ?? 0);
    const err = String(payout.errorMessage || payout.ErrorMessage || '').trim();
    if (failed === 0 && held === 0) {
        if (paid > 0) {
            toast.success(`Pooled tips paid to ${paid} recipient${paid === 1 ? '' : 's'}.`);
        }
        return;
    }
    const parts = [];
    if (paid > 0) parts.push(`${paid} paid`);
    if (failed > 0) parts.push(`${failed} failed`);
    if (held > 0) parts.push(`${held} waiting for full share funds`);
    const detail = err ? ` ${err}` : '';
    toast.warn(`Pooled tips: ${parts.join(', ')}.${detail} Use Pay pooled tips to retry.`);
}

export function endWorkperiodRequest(periodId, onSuccess) {
    return (dispatch) => {
        dispatch({ type: ENDING_WORKPERIOD });

        axios.put(`/api/admin/workperiod/end/${periodId}`)
            .then((res) => {
                if (res.data?.status === 'Error') {
                    toast.error(res.data?.message || 'Failed to end work period');
                    dispatch({ type: FAILURE_END_WORKPERIOD, payload: res.data?.message });
                    return;
                }
                toast.success('Work period ended successfully. All users have been clocked out.');
                toastWorkperiodClosePooledPayout(res.data);
                dispatch({ type: ENDED_WORKPERIOD });
                if (typeof onSuccess === 'function') {
                    onSuccess();
                } else {
                    // Default: refresh detail and users (used from WorkperiodDetail page)
                    dispatch(getWorkperiodDetailRequest(periodId));
                    dispatch(getWorkperiodUsersRequest(periodId));
                }
            })
            .catch((err) => {
                console.error('Error ending work period:', err);
                toast.error('Failed to end work period');
                dispatch({ type: FAILURE_END_WORKPERIOD, payload: err?.message || 'Failed to end work period' });
            });
    };
}

export function startWorkperiodRequest(onSuccess) {
    return (dispatch) => {
        dispatch({ type: STARTING_WORKPERIOD });

        axios.post('/api/admin/workperiod/begin')
            .then((res) => {
                if (res.data?.status === 'Error') {
                    toast.error(res.data?.message || 'Failed to start work period');
                    dispatch({ type: FAILURE_START_WORKPERIOD, payload: res.data?.message });
                    return;
                }
                toast.success('New work period started successfully.');
                dispatch({ type: STARTED_WORKPERIOD });
                if (onSuccess) onSuccess(res.data);
            })
            .catch((err) => {
                console.error('Error starting work period:', err);
                toast.error('Failed to start work period');
                dispatch({ type: FAILURE_START_WORKPERIOD, payload: err?.message || 'Failed to start work period' });
            });
    };
}

export function downloadWorkperiodCloseoutRequest(periodId) {
    return async (dispatch) => {
        dispatch({ type: DOWNLOADING_WORKPERIOD_CLOSEOUT });

        const fallbackFileName = `POS_Closeout_${periodId}.pdf`;
        const endpoint = `/api/reports/workperiod/${periodId}/closeout/pdf`;

        try {
            const res = await axios.get(endpoint, {
                responseType: 'blob',
                headers: {
                    Accept: 'application/pdf',
                },
            });

            const contentType = String(res.headers?.['content-type'] || '').toLowerCase();

            if (!contentType.includes('pdf')) {
                const message = await getCloseoutErrorMessage({ response: { data: res.data }, message: 'Failed to download closeout summary' });
                toast.error(message);
                dispatch({ type: FAILURE_WORKPERIOD_CLOSEOUT, payload: message });
                return;
            }

            const fileName = getFileNameFromDisposition(
                res.headers?.['content-disposition'],
                fallbackFileName,
            );

            triggerBrowserDownload(res.data, fileName);
            toast.success('Closeout summary downloaded');
            dispatch({ type: DOWNLOADED_WORKPERIOD_CLOSEOUT });
        } catch (err) {
            console.error('Error downloading work period closeout:', err);
            const message = await getCloseoutErrorMessage(err);
            toast.error(message);
            dispatch({ type: FAILURE_WORKPERIOD_CLOSEOUT, payload: message });
        }
    };
}

export function downloadWorkperiodPooledTipReportRequest(periodId) {
    return async () => {
        const fallbackFileName = `Pooled_Tips_${periodId}.pdf`;
        const endpoint = `/api/reports/workperiod/${periodId}/tip-pool/pdf`;

        try {
            const res = await axios.get(endpoint, {
                responseType: 'blob',
                headers: {
                    Accept: 'application/pdf',
                },
            });

            const contentType = String(res.headers?.['content-type'] || '').toLowerCase();

            if (!contentType.includes('pdf')) {
                const message = await getCloseoutErrorMessage({
                    response: { data: res.data },
                    message: 'Failed to download pooled tip report',
                });
                throw Object.assign(new Error(message), { toasted: false, response: { data: res.data } });
            }

            const fileName = getFileNameFromDisposition(
                res.headers?.['content-disposition'],
                fallbackFileName,
            );

            triggerBrowserDownload(res.data, fileName);
            toast.success('Pooled tip report downloaded');
        } catch (err) {
            console.error('Error downloading pooled tip report:', err);
            const message = await getCloseoutErrorMessage({
                response: err?.response,
                message: err?.message || 'Failed to download pooled tip report',
            });
            toast.error(message);
            throw err;
        }
    };
}

export function sendWorkperiodPooledTipReportRequest(periodId, channel) {
    return async () => {
        const path = channel === 'sms' ? 'send-sms' : 'send-email';
        const endpoint = `/api/reports/workperiod/${periodId}/tip-pool/${path}`;
        try {
            const res = await axios.post(endpoint);
            if (res.data?.status === 'Ok') {
                return {
                    sent: Number(res.data.sent) || 0,
                    skipped: Number(res.data.skipped) || 0,
                    failed: Number(res.data.failed) || 0,
                };
            }
            throw new Error(res.data?.message || 'Failed to send pooled tip report');
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || 'Failed to send pooled tip report';
            toast.error(message);
            throw err;
        }
    };
}

/**
 * Manually settle pooled tip Stripe shares for a work period.
 * Optional userId pays only that employee's share (option A).
 * Requires Pooled Tips + platform tip routing (auto-transfer to employees).
 */
export function payWorkperiodPooledTipsRequest(periodId, userId = null) {
    return async () => {
        const endpoint = `/api/admin/workperiod/${periodId}/pay-pooled-tips`;
        try {
            // Always send a JSON object — an undefined body posts with no Content-Type,
            // which Web API formatter binding can reject.
            const body = userId != null && userId !== ''
                ? { userId: Number(userId) }
                : {};
            const res = await axios.post(endpoint, body);
            if (res.data?.status === 'Error') {
                throw new Error(res.data?.message || 'Failed to pay pooled tips');
            }

            const result = res.data?.result || res.data?.Result || {};
            return {
                success: result.success ?? result.Success ?? true,
                completed: Number(result.transfersCompleted ?? result.TransfersCompleted) || 0,
                failed: Number(result.transfersFailed ?? result.TransfersFailed) || 0,
                skipped: Number(result.employeesSkipped ?? result.EmployeesSkipped) || 0,
                held: Number(result.heldCount ?? result.HeldCount) || 0,
                processed: Number(result.employeesProcessed ?? result.EmployeesProcessed) || 0,
                message: result.errorMessage || result.ErrorMessage || '',
                employeeResults: result.employeeResults || result.EmployeeResults || [],
            };
        } catch (err) {
            const message =
                err?.response?.data?.message
                || err?.message
                || 'Failed to pay pooled tips';
            toast.error(message);
            throw err;
        }
    };
}

/**
 * One employee's pooled tip share + settlement status for a work period.
 */
export function getWorkperiodPooledTipShareRequest(periodId, userId) {
    return async () => {
        const endpoint = `/api/admin/workperiod/${periodId}/pooled-tip-share/${userId}`;
        try {
            const res = await axios.get(endpoint);
            if (res.data?.status === 'Error') {
                throw new Error(res.data?.message || 'Failed to load pooled tip share');
            }
            const raw = res.data?.share || res.data?.Share || {};
            const shareAmount = Number(raw.shareAmount ?? raw.ShareAmount) || 0;
            const payableRaw = raw.payableAmount ?? raw.PayableAmount;
            const payableAmount = payableRaw === undefined || payableRaw === null
                ? shareAmount
                : (Number(payableRaw) || 0);
            const alreadySettled = Number(
                raw.alreadySettledOutsidePooledAmount ?? raw.AlreadySettledOutsidePooledAmount
            ) || Math.max(0, shareAmount - payableAmount);
            return {
                pooledTipsEnabled: !!(raw.pooledTipsEnabled ?? raw.PooledTipsEnabled),
                merchantId: Number(raw.merchantId ?? raw.MerchantId) || 0,
                workPeriodId: Number(raw.workPeriodId ?? raw.WorkPeriodId) || 0,
                userId: Number(raw.userId ?? raw.UserId) || 0,
                periodEnded: !!(raw.periodEnded ?? raw.PeriodEnded),
                shareAmount,
                payableAmount,
                alreadySettledOutsidePooledAmount: alreadySettled,
                tipAmount: Number(raw.tipAmount ?? raw.TipAmount) || 0,
                gratuityAmount: Number(raw.gratuityAmount ?? raw.GratuityAmount) || 0,
                status: String(raw.status ?? raw.Status ?? 'none').toLowerCase(),
                message: raw.message || raw.Message || '',
                stripeTransferId: raw.stripeTransferId || raw.StripeTransferId || null,
                transferId: raw.transferId ?? raw.TransferId ?? null,
                paidToManager: !!(raw.paidToManager ?? raw.PaidToManager),
                employeePaid: !!(raw.employeePaid ?? raw.EmployeePaid),
                showPayButton: !!(raw.showPayButton ?? raw.ShowPayButton),
                showManualPay: !!(raw.showManualPay ?? raw.ShowManualPay),
                canPay: !!(raw.canPay ?? raw.CanPay),
                blockReasons: raw.blockReasons || raw.BlockReasons || [],
            };
        } catch (err) {
            const message =
                err?.response?.data?.message
                || err?.message
                || 'Failed to load pooled tip share';
            throw new Error(message);
        }
    };
}

export function getWorkperiodPooledTipsAvailabilityRequest(periodId) {
    return async () => {
        const endpoint = `/api/admin/workperiod/${periodId}/pooled-tips-availability`;
        try {
            const res = await axios.get(endpoint);
            if (res.data?.status === 'Error') {
                throw new Error(res.data?.message || 'Failed to load pooled tip availability');
            }
            const raw = res.data?.availability || res.data?.Availability || {};
            return {
                pooledTipsEnabled: !!(raw.pooledTipsEnabled ?? raw.PooledTipsEnabled),
                periodEnded: !!(raw.periodEnded ?? raw.PeriodEnded),
                unpaidShareCount: Number(raw.unpaidShareCount ?? raw.UnpaidShareCount) || 0,
                showPayButton: !!(raw.showPayButton ?? raw.ShowPayButton),
            };
        } catch (err) {
            const message =
                err?.response?.data?.message
                || err?.message
                || 'Failed to load pooled tip availability';
            throw new Error(message);
        }
    };
}
